import React, { useEffect, useState } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import Meta from '../components/Meta';
import { getOrderDetails, submitRefundRequest, resetOrderFlags } from '../slices/orderSlice';

const OrderRefundRequestScreen = ({ match, history }) => {
  const orderId = match.params.id;
  const dispatch = useDispatch();

  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [localError, setLocalError] = useState('');
  // State for item selections
  const [selectedItems, setSelectedItems] = useState({}); // { itemId: { selected: true, quantity: 2 } }

  const orderState = useSelector((state) => state.order);
  const {
    orderDetails: order,
    loading,
    error,
    loadingRefundRequest,
    successRefundRequest,
  } = orderState;

  useEffect(() => {
    if (!orderId) return;
    if (!order?._id || String(order._id) !== String(orderId)) {
      dispatch(getOrderDetails(orderId));
    }
  }, [dispatch, orderId, order?._id]);

  // Initialize item selections when order loads
  useEffect(() => {
    if (order?.orderItems?.length > 0 && Object.keys(selectedItems).length === 0) {
      const initialSelections = {};
      order.orderItems.forEach(item => {
        initialSelections[item._id] = {
          selected: false,
          quantity: item.qty,
        };
      });
      setSelectedItems(initialSelections);
    }
  }, [order?.orderItems, selectedItems]);

  useEffect(() => {
    if (successRefundRequest) {
      dispatch(resetOrderFlags());
      history.push(`/order/${orderId}`);
    }
  }, [dispatch, history, orderId, successRefundRequest]);

  const refundStatus = String(order?.refundRequest?.status || 'none');
  const notEligible = !(order?.isDelivered && order?.isPaid);

  // Item selection handlers
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        selected: !prev[itemId]?.selected,
      },
    }));
  };

  const updateItemQuantity = (itemId, quantity) => {
    const item = order.orderItems.find(i => i._id === itemId);
    const maxQty = item?.qty || 1;
    const validQty = Math.min(Math.max(1, parseInt(quantity) || 1), maxQty);
    
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: validQty,
      },
    }));
  };

  const selectAllItems = () => {
    const allSelected = {};
    order.orderItems.forEach(item => {
      allSelected[item._id] = {
        selected: true,
        quantity: item.qty,
      };
    });
    setSelectedItems(allSelected);
  };

  const deselectAllItems = () => {
    const noneSelected = {};
    order.orderItems.forEach(item => {
      noneSelected[item._id] = {
        selected: false,
        quantity: item.qty,
      };
    });
    setSelectedItems(noneSelected);
  };

  // Calculate refund amount
  const calculateRefundAmount = () => {
    let total = 0;
    order.orderItems.forEach(item => {
      const selection = selectedItems[item._id];
      if (selection?.selected) {
        total += item.price * selection.quantity;
      }
    });
    return total.toFixed(2);
  };

  const hasSelectedItems = Object.values(selectedItems).some(item => item.selected);

  const submitHandler = (e) => {
    e.preventDefault();
    setLocalError('');

    const r = String(reason || '').trim();
    const d = String(description || '').trim();

    if (!r) {
      setLocalError('Please provide a reason.');
      return;
    }
    
    if (!hasSelectedItems) {
      setLocalError('Please select at least one item to refund.');
      return;
    }

    if (notEligible) {
      setLocalError('Refund requests are only available after delivery.');
      return;
    }
    if (refundStatus === 'pending') {
      setLocalError('A refund request is already pending for this order.');
      return;
    }
    if (refundStatus === 'refunded') {
      setLocalError('This order has already been refunded.');
      return;
    }

    // Build items array with selected items and quantities
    const itemsToRefund = order.orderItems
      .filter(item => selectedItems[item._id]?.selected)
      .map(item => ({
        product: item.product,
        name: item.name,
        quantity: selectedItems[item._id].quantity,
        price: item.price,
      }));

    dispatch(submitRefundRequest({ 
      orderId, 
      reason: r, 
      description: d,
      items: itemsToRefund,
      refundAmount: calculateRefundAmount(),
    }));
  };

  return (
    <>
      <Meta title='Request Refund' robots='noindex,follow' />
      <h1>Request Refund</h1>

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{error}</Message>
      ) : order?.status === 'refunded' ? (
        <Message variant='success'>
          This order has already been refunded.
          <div className='mt-3'>
            <Button variant='secondary' onClick={() => history.push(`/order/${orderId}`)}>
              Back to Order
            </Button>
          </div>
        </Message>
      ) : (
        <Card className='p-3'>
          {localError && <Message variant='danger'>{localError}</Message>}

          {/* Item Selection Section */}
          {order?.orderItems?.length > 0 && (
            <div className='mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='mb-0'>Select Items to Refund</h5>
                <div className='d-flex gap-2'>
                  <Button 
                    size='sm' 
                    variant='outline-primary'
                    onClick={selectAllItems}
                    disabled={notEligible || refundStatus === 'pending' || refundStatus === 'refunded'}
                  >
                    Select All
                  </Button>
                  <Button 
                    size='sm' 
                    variant='outline-secondary'
                    onClick={deselectAllItems}
                    disabled={notEligible || refundStatus === 'pending' || refundStatus === 'refunded'}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              <div className='border rounded p-3 bg-light'>
                {order.orderItems.map((item) => {
                  const selection = selectedItems[item._id] || { selected: false, quantity: item.qty };
                  return (
                    <Card key={item._id} className='mb-3'>
                      <Card.Body>
                        <div className='d-flex align-items-start gap-3'>
                          {/* Checkbox */}
                          <Form.Check
                            type='checkbox'
                            checked={selection.selected}
                            onChange={() => toggleItemSelection(item._id)}
                            disabled={notEligible || refundStatus === 'pending' || refundStatus === 'refunded'}
                            className='mt-1'
                          />

                          {/* Item Image */}
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                            className='rounded'
                          />

                          {/* Item Details */}
                          <div className='flex-grow-1'>
                            <h6 className='mb-1'>{item.name}</h6>
                            <div className='text-muted small'>
                              <div>Price: ${item.price}</div>
                              <div>Ordered Qty: {item.qty}</div>
                            </div>
                          </div>

                          {/* Quantity Selector */}
                          <div style={{ minWidth: '150px' }}>
                            <Form.Label className='small mb-1'>Refund Quantity</Form.Label>
                            <Form.Control
                              type='number'
                              size='sm'
                              min='1'
                              max={item.qty}
                              value={selection.quantity}
                              onChange={(e) => updateItemQuantity(item._id, e.target.value)}
                              disabled={!selection.selected || notEligible || refundStatus === 'pending' || refundStatus === 'refunded'}
                              style={{ width: '80px' }}
                            />
                          </div>

                          {/* Item Total */}
                          <div className='text-end' style={{ minWidth: '100px' }}>
                            <div className='small text-muted'>Item Total</div>
                            <div className='fw-bold'>
                              ${(item.price * (selection.selected ? selection.quantity : 0)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}

                {/* Refund Summary */}
                <div className='mt-3 p-3 bg-white rounded border'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      <h6 className='mb-1'>Refund Amount</h6>
                      <small className='text-muted'>
                        {hasSelectedItems 
                          ? `${Object.values(selectedItems).filter(s => s.selected).length} item(s) selected`
                          : 'No items selected'}
                      </small>
                    </div>
                    <h4 className='mb-0 text-primary'>${hasSelectedItems ? calculateRefundAmount() : '0.00'}</h4>
                  </div>
                </div>
              </div>
            </div>
          )}

          {notEligible && (
            <Message variant='warning'>Refund requests are only available after delivery.</Message>
          )}

          {refundStatus === 'pending' && (
            <Message variant='info'>A refund request is already pending for this order.</Message>
          )}

          {refundStatus === 'refunded' && (
            <Message variant='success'>This order has been refunded.</Message>
          )}

          <Form onSubmit={submitHandler}>
            <Form.Group controlId='refundReason'>
              <Form.Label>Reason (required)</Form.Label>
              <Form.Control
                as='select'
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={notEligible || refundStatus === 'pending' || refundStatus === 'refunded'}
              >
                <option value=''>Select a reason</option>
                <option value='Item arrived damaged'>Item arrived damaged</option>
                <option value='Wrong item received'>Wrong item received</option>
                <option value='Missing items'>Missing items</option>
                <option value='Other'>Other</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId='refundDescription' className='mt-3'>
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={notEligible || refundStatus === 'pending' || refundStatus === 'refunded'}
                placeholder='Add any helpful details (optional)'
              />
            </Form.Group>

            <div className='mt-3 d-flex align-items-center gap-2'>
              <Button
                type='submit'
                variant='primary'
                disabled={loadingRefundRequest || notEligible || refundStatus === 'pending' || refundStatus === 'refunded' || !hasSelectedItems}
              >
                Submit Refund Request
              </Button>
              {loadingRefundRequest && <Loader />}
              <Button
                type='button'
                variant='secondary'
                onClick={() => history.push(`/order/${orderId}`)}
              >
                Back
              </Button>
            </div>
          </Form>
        </Card>
      )}
    </>
  );
};

export default OrderRefundRequestScreen;
